import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import { getAuth, onAuthStateChanged, User } from "firebase/auth";
import { GetUserAccess } from "@/app/api/ACLs";
import { app } from "@/app/firebase/config";
import { Unsubscribe } from "@firebase/util";

interface UserDetails {
  email: string;
  displayName: string;
}

interface UserStore {
  user: User | null;
  setUser: (user: User | null) => void;

  userDetails: UserDetails | null;
  setUserDetails: (userDetails: UserDetails | null) => void;

  userIsSuperAdmin: boolean;
  setUserIsSuperAdmin: (superAdmin: boolean) => void;

  userIsOrgAdmin: boolean;
  setUserIsOrgAdmin: (orgAdmin: boolean) => void;

  adminOrgCodes: string[];
  setAdminOrgCodes: (orgCodes: string[]) => void;

  loading: boolean;
  setLoading: (loading: boolean) => void;

  hasHydrated: boolean;
  setHasHydrated: (hydrated: boolean) => void;

  initializeAuth: () => Unsubscribe;
}

const auth = getAuth(app);

export const useUserStore = create<UserStore>()(
  devtools(
    persist(
      (set) => ({
        user: null,
        setUser: (user) => set({ user }),

        userDetails: null,
        setUserDetails: (userDetails) => set({ userDetails: userDetails }),

        userIsSuperAdmin: false,
        setUserIsSuperAdmin: (superAdmin) =>
          set({ userIsSuperAdmin: superAdmin }),

        userIsOrgAdmin: false,
        setUserIsOrgAdmin: (orgAdmin) => set({ userIsOrgAdmin: orgAdmin }),

        adminOrgCodes: [],
        setAdminOrgCodes: (orgCodes) => set({ adminOrgCodes: orgCodes }),

        loading: true,
        setLoading: (loading) => set({ loading }),

        hasHydrated: false,
        setHasHydrated: (hydrated: boolean) => set({ hasHydrated: hydrated }),

        initializeAuth: () => {
          set({ loading: true });

          return onAuthStateChanged(auth, async (user: User | null) => {
            set({ user });

            if (user) {
              const userAccess = await GetUserAccess();
              const userACLs = userAccess.userRoles;

              const orgAdminCodes: string[] = [];
              userACLs?.orgAccess.forEach((a) => {
                orgAdminCodes.push(...a.collegeOrgCode);
                orgAdminCodes.push(...a.collegeOrgCode);
              });

              set({
                userIsSuperAdmin:
                  userACLs && userACLs.applicationRoles.length > 0,
                userIsOrgAdmin: userACLs && userACLs.orgRoles.length > 0,
                adminOrgCodes: orgAdminCodes,
                userDetails: {
                  email: user.email || "",
                  displayName: user.displayName || "",
                },
                loading: false,
              });
            } else {
              set({
                userIsSuperAdmin: false,
                adminOrgCodes: [],
                userIsOrgAdmin: false,
                userDetails: null,
                loading: false,
              });
            }
          });
        },
      }),
      {
        name: "user-storage",
        onRehydrateStorage: () => (state) => {
          state?.setHasHydrated(true);
        },
      },
    ),
  ),
);
